import { useCallback, useEffect, useRef, useState } from 'react';

interface BrowserSpeechRecognitionAlternative {
	transcript: string;
}

interface BrowserSpeechRecognitionResult {
	[index: number]: BrowserSpeechRecognitionAlternative;
}

interface BrowserSpeechRecognitionEvent {
	results: ArrayLike<BrowserSpeechRecognitionResult>;
}

interface BrowserSpeechRecognition {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
	onerror: ((event: { error: string }) => void) | null;
	onend: (() => void) | null;
	start: () => void;
	stop: () => void;
	abort: () => void;
}

interface BrowserSpeechRecognitionConstructor {
	new (): BrowserSpeechRecognition;
}

type SpeechRecognitionWindow = Window & {
	SpeechRecognition?: BrowserSpeechRecognitionConstructor;
	webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
};

export interface UseSpeechRecognitionOptions {
	value: string;
	onChange: (value: string) => void;
	onError?: (message: string) => void;
	language?: string;
}

const getSpeechRecognitionConstructor = () => {
	if (typeof window === 'undefined') {
		return undefined;
	}

	const speechWindow = window as SpeechRecognitionWindow;
	return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
};

const getSpeechRecognitionErrorMessage = (error: string) => {
	switch (error) {
		case 'not-allowed':
		case 'service-not-allowed':
			return 'Microphone permission was denied.';
		case 'audio-capture':
			return 'No microphone is available.';
		case 'no-speech':
			return 'No speech was detected. Try again.';
		default:
			return 'Voice transcription failed. Try again.';
	}
};

export default function useSpeechRecognition({ value, onChange, onError, language }: UseSpeechRecognitionOptions) {
	const [isListening, setIsListening] = useState(false);
	const [isSupported] = useState(() => Boolean(getSpeechRecognitionConstructor()));
	const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
	const inputBaseRef = useRef('');
	const valueRef = useRef(value);
	const onChangeRef = useRef(onChange);
	const onErrorRef = useRef(onError);

	valueRef.current = value;
	onChangeRef.current = onChange;
	onErrorRef.current = onError;

	const stop = useCallback(() => {
		recognitionRef.current?.stop();
		setIsListening(false);
	}, []);

	const toggle = useCallback(() => {
		if (isListening) {
			stop();
			return;
		}

		const SpeechRecognition = getSpeechRecognitionConstructor();
		if (!SpeechRecognition) {
			return;
		}

		const recognition = new SpeechRecognition();
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = language ?? navigator.language ?? 'en-US';
		inputBaseRef.current = valueRef.current.trimEnd();
		recognition.onresult = (event) => {
			let transcript = '';
			for (let index = 0; index < event.results.length; index += 1) {
				transcript += event.results[index][0]?.transcript ?? '';
			}

			const base = inputBaseRef.current;
			onChangeRef.current(`${base}${base && transcript ? ' ' : ''}${transcript}`);
		};
		recognition.onerror = (event) => {
			setIsListening(false);
			if (event.error !== 'aborted') {
				onErrorRef.current?.(getSpeechRecognitionErrorMessage(event.error));
			}
		};
		recognition.onend = () => {
			setIsListening(false);
			if (recognitionRef.current === recognition) {
				recognitionRef.current = null;
			}
		};

		recognitionRef.current = recognition;
		try {
			recognition.start();
			setIsListening(true);
		} catch {
			recognitionRef.current = null;
			onErrorRef.current?.('Voice transcription could not be started.');
		}
	}, [isListening, language, stop]);

	useEffect(
		() => () => {
			const recognition = recognitionRef.current;
			if (recognition) {
				recognition.onresult = null;
				recognition.onerror = null;
				recognition.onend = null;
				recognition.abort();
			}
		},
		[],
	);

	return { isSupported, isListening, toggle, stop };
}
